/**
 * Keeps the property table from jumping after archive/unarchive.
 *
 * Archiving a unit can change where a row sorts on the server. If we refetch right away, the
 * row often moves (or leaves the loaded pages). Instead we update the cached list in place so
 * the row stays put, then mark the data as needing a refresh later — without fetching now.
 */

import { ARCHIVE_STALE_ONLY_TAGS } from '../../../constants/InvalidateConstants';

import type { QueryClient } from '@zg-rentals/react-query5';
import type {
  AccountViewPropertiesResponseDto,
  AccountViewPropertyResponseDto,
  AccountViewPropertyUnitsResponseDto,
  ListingAlias,
  ListingCountDto,
} from '@zg-rentals/ts-rental-manager-api-client';

export type ArchiveScope = 'property' | 'unit';

type PropertyTableListInfiniteData = {
  pages: Array<AccountViewPropertiesResponseDto>;
  pageParams?: Array<unknown>;
};

type PropertyTableUnitsListInfiniteData = {
  pages: Array<AccountViewPropertyUnitsResponseDto>;
  pageParams?: Array<unknown>;
};

type ApplyArchiveCacheUpdatesArgs = {
  scope: ArchiveScope;
  groupId: string;
  listingAlias: ListingAlias;
  isUnarchiving: boolean;
};

const matchesPropertyRow = (
  property: AccountViewPropertyResponseDto,
  { groupId, listingAlias }: Pick<ApplyArchiveCacheUpdatesArgs, 'groupId' | 'listingAlias'>,
): boolean => {
  if (groupId) {
    return property.groupId === groupId;
  }
  return property.listingAlias === listingAlias;
};

const clampNonNegative = (value: number): number => Math.max(0, value);

/** Moves listings between the active and archived counts used by the toggle / empty state. */
export const updateListingCount = (
  listingCount: ListingCountDto,
  { delta, isUnarchiving }: { delta: number; isUnarchiving: boolean },
): ListingCountDto => {
  if (isUnarchiving) {
    return {
      ...listingCount,
      total: listingCount.total + delta,
      archived: clampNonNegative(listingCount.archived - delta),
    };
  }

  return {
    ...listingCount,
    total: clampNonNegative(listingCount.total - delta),
    archived: listingCount.archived + delta,
  };
};

/**
 * Updates one page of property rows.
 * - Whole property (or last unit): remove the row.
 * - One unit in a multi-unit property: keep the row where it is and drop the unit count by 1.
 * Leaves the summary counts alone — those are applied on the first page only.
 */
export const updatePropertyTableListPage = (
  page: AccountViewPropertiesResponseDto,
  args: ApplyArchiveCacheUpdatesArgs,
): { page: AccountViewPropertiesResponseDto; delta: number | null } => {
  const propertyIndex = page.properties.findIndex((property) => matchesPropertyRow(property, args));
  if (propertyIndex < 0) {
    return { page, delta: null };
  }

  const property = page.properties[propertyIndex];
  const shouldRemove = args.scope === 'property' || (args.scope === 'unit' && property.unitCount.total <= 1);
  const delta = args.scope === 'property' ? property.unitCount.total : 1;

  if (shouldRemove) {
    return {
      delta,
      page: {
        ...page,
        properties: page.properties.filter((_, index) => index !== propertyIndex),
      },
    };
  }

  return {
    delta,
    page: {
      ...page,
      properties: page.properties.map((row, index) => {
        if (index !== propertyIndex) {
          return row;
        }
        return {
          ...row,
          unitCount: {
            ...row.unitCount,
            total: clampNonNegative(row.unitCount.total - 1),
          },
        };
      }),
    },
  };
};

/**
 * Walks every loaded page, updates the matching row, and writes the count change on the first
 * page (that's the only place the UI reads the summary from).
 */
export const updatePropertyTableListPages = (
  pages: Array<AccountViewPropertiesResponseDto>,
  args: ApplyArchiveCacheUpdatesArgs,
): Array<AccountViewPropertiesResponseDto> => {
  let delta: number | null = null;
  const nextPages = pages.map((page) => {
    const result = updatePropertyTableListPage(page, args);
    if (result.delta !== null) {
      delta = result.delta;
    }
    return result.page;
  });

  if (delta === null || nextPages.length === 0) {
    return pages;
  }

  const listingCountDelta = delta;

  return nextPages.map((page, index) => {
    if (index !== 0) {
      return page;
    }
    return {
      ...page,
      listingCount: updateListingCount(page.listingCount, {
        delta: listingCountDelta,
        isUnarchiving: args.isUnarchiving,
      }),
    };
  });
};

/**
 * Removes one unit from a Units-view page. Summary counts are applied on the first page only.
 */
export const updatePropertyTableUnitsListPage = (
  page: AccountViewPropertyUnitsResponseDto,
  listingAlias: ListingAlias,
): { page: AccountViewPropertyUnitsResponseDto; matched: boolean } => {
  const unitIndex = page.units.findIndex((unit) => unit.listingAlias === listingAlias);
  if (unitIndex < 0) {
    return { page, matched: false };
  }

  return {
    matched: true,
    page: {
      ...page,
      units: page.units.filter((_, index) => index !== unitIndex),
    },
  };
};

/** Same as the properties-list walk, but for the flat Units view. */
export const updatePropertyTableUnitsListPages = (
  pages: Array<AccountViewPropertyUnitsResponseDto>,
  args: Pick<ApplyArchiveCacheUpdatesArgs, 'listingAlias' | 'isUnarchiving'>,
): Array<AccountViewPropertyUnitsResponseDto> => {
  let matched = false;
  const nextPages = pages.map((page) => {
    const result = updatePropertyTableUnitsListPage(page, args.listingAlias);
    if (result.matched) {
      matched = true;
    }
    return result.page;
  });

  if (!matched || nextPages.length === 0) {
    return pages;
  }

  return nextPages.map((page, index) => {
    if (index !== 0) {
      return page;
    }
    return {
      ...page,
      listingCount: updateListingCount(page.listingCount, {
        delta: 1,
        isUnarchiving: args.isUnarchiving,
      }),
    };
  });
};

const patchPropertyTableList = (queryClient: QueryClient, args: ApplyArchiveCacheUpdatesArgs) => {
  queryClient.setQueriesData<PropertyTableListInfiniteData>({ queryKey: ['getPropertyTableList'] }, (data) => {
    if (!data?.pages?.length) {
      return data;
    }

    return {
      ...data,
      pages: updatePropertyTableListPages(data.pages, args),
    };
  });
};

const patchPropertyTableUnitsList = (queryClient: QueryClient, args: ApplyArchiveCacheUpdatesArgs) => {
  queryClient.setQueriesData<PropertyTableUnitsListInfiniteData>(
    { queryKey: ['getPropertyTableUnitsList'] },
    (data) => {
      if (!data?.pages?.length) {
        return data;
      }

      return {
        ...data,
        pages: updatePropertyTableUnitsListPages(data.pages, args),
      };
    },
  );
};

/**
 * After a successful archive/unarchive: edit the lists we already have, then mark them outdated
 * without reloading. That keeps rows from jumping. Fresh data loads later when the user
 * remounts, changes filters/search, or switches views.
 */
export const applyArchiveCacheUpdates = (queryClient: QueryClient, args: ApplyArchiveCacheUpdatesArgs) => {
  patchPropertyTableList(queryClient, args);

  // Units view: we know which alias left, so patch that list too.
  if (args.scope === 'unit') {
    patchPropertyTableUnitsList(queryClient, args);
  }

  // Mark outdated but do not refetch — keep the edited order until the next natural reload.
  queryClient.invalidateQueries({
    predicate: ({ meta }) =>
      Boolean(meta?.tags?.some((tag) => (ARCHIVE_STALE_ONLY_TAGS as ReadonlyArray<string>).includes(tag))),
    refetchType: 'none',
  });

  // Whole-property archive may drop many units; we don't know every alias, so reload that list.
  if (args.scope === 'property') {
    queryClient.invalidateQueries({
      predicate: ({ meta }) => Boolean(meta?.tags?.includes('getPropertyTableUnitsList')),
    });
  }
};
